'use client';
import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';

// ============ DATA ============
interface ScheduleEntry {
  departure: string;
  arrival: string;
  duration: string;
  price: number;
  days: string;
  class?: string;
}

interface LineSchedule {
  id: string;
  type: 'Train' | 'Métro' | 'Tram';
  name: string;
  nameAr: string;
  from: string;
  to: string;
  operator: string;
  color: string;
  schedules: ScheduleEntry[];
  stops: string[];
  frequency?: string;
  firstTrain?: string;
  lastTrain?: string;
  notes?: string;
}

const SCHEDULES: LineSchedule[] = [
  // SNTF TRAINS
  {
    id: 'sntf-alger-oran',
    type: 'Train',
    name: 'Alger – Oran',
    nameAr: 'الجزائر – وهران',
    from: 'Alger Centre',
    to: 'Oran',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '18:00',
    stops: ['Blida', 'Boufarik', 'Chlef', 'Relizane', 'Mostaganem', 'Oran'],
    schedules: [
      { departure: '06:00', arrival: '09:45', duration: '3h45', price: 750, days: 'Tous les jours', class: '1ère classe' },
      { departure: '08:30', arrival: '12:15', duration: '3h45', price: 750, days: 'Tous les jours', class: '2ème classe' },
      { departure: '12:00', arrival: '15:45', duration: '3h45', price: 750, days: 'Tous les jours', class: '2ème classe' },
      { departure: '15:30', arrival: '19:15', duration: '3h45', price: 750, days: 'Tous les jours', class: '2ème classe' },
      { departure: '18:00', arrival: '21:45', duration: '3h45', price: 750, days: 'Tous les jours', class: '2ème classe' },
    ],
  },
  {
    id: 'sntf-alger-constantine',
    type: 'Train',
    name: 'Alger – Constantine',
    nameAr: 'الجزائر – قسنطينة',
    from: 'Alger Centre',
    to: 'Constantine',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '17:00',
    stops: ['Boumerdès', 'Thénia', 'Boudouaou', 'Bordj Bou Arreridj', 'Sétif', 'Constantine'],
    schedules: [
      { departure: '06:00', arrival: '10:20', duration: '4h20', price: 850, days: 'Tous les jours', class: '1ère classe' },
      { departure: '07:30', arrival: '11:50', duration: '4h20', price: 850, days: 'Tous les jours', class: '2ème classe' },
      { departure: '12:00', arrival: '16:20', duration: '4h20', price: 850, days: 'Tous les jours', class: '2ème classe' },
      { departure: '17:00', arrival: '21:20', duration: '4h20', price: 850, days: 'Tous les jours', class: '2ème classe' },
    ],
  },
  {
    id: 'sntf-alger-annaba',
    type: 'Train',
    name: 'Alger – Annaba',
    nameAr: 'الجزائر – عنابة',
    from: 'Alger Centre',
    to: 'Annaba',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '15:00',
    stops: ['Thénia', 'Béjaïa', 'Jijel', 'Skikda', 'Guelma', 'Annaba'],
    schedules: [
      { departure: '06:00', arrival: '11:30', duration: '5h30', price: 950, days: 'Tous les jours', class: '1ère classe' },
      { departure: '10:00', arrival: '15:30', duration: '5h30', price: 950, days: 'Tous les jours', class: '2ème classe' },
      { departure: '15:00', arrival: '20:30', duration: '5h30', price: 950, days: 'Tous les jours', class: '2ème classe' },
    ],
  },
  {
    id: 'sntf-alger-blida',
    type: 'Train',
    name: 'Alger – Blida',
    nameAr: 'الجزائر – البليدة',
    from: 'Alger Centre',
    to: 'Blida',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '05:30',
    lastTrain: '22:00',
    frequency: 'Toutes les heures',
    stops: ['Birtouta', 'Zéralda', 'Boufarik', 'Blida'],
    schedules: [
      { departure: '05:30', arrival: '06:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '06:30', arrival: '07:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '07:30', arrival: '08:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '08:30', arrival: '09:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '09:30', arrival: '10:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '10:30', arrival: '11:40', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '12:00', arrival: '13:10', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '14:00', arrival: '15:10', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '16:00', arrival: '17:10', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '18:00', arrival: '19:10', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '20:00', arrival: '21:10', duration: '1h10', price: 250, days: 'Tous les jours' },
      { departure: '22:00', arrival: '23:10', duration: '1h10', price: 250, days: 'Tous les jours' },
    ],
  },
  {
    id: 'sntf-alger-bejaia',
    type: 'Train',
    name: 'Alger – Béjaïa',
    nameAr: 'الجزائر – بجاية',
    from: 'Alger Centre',
    to: 'Béjaïa',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '07:00',
    lastTrain: '15:00',
    stops: ['Boumerdès', 'Thénia', 'Boudouaou', 'Béjaïa'],
    schedules: [
      { departure: '07:00', arrival: '10:00', duration: '3h00', price: 600, days: 'Tous les jours' },
      { departure: '11:00', arrival: '14:00', duration: '3h00', price: 600, days: 'Tous les jours' },
      { departure: '15:00', arrival: '18:00', duration: '3h00', price: 600, days: 'Tous les jours' },
    ],
  },
  {
    id: 'sntf-alger-reghaïa',
    type: 'Train',
    name: 'Alger – Réghaïa',
    nameAr: 'الجزائر – رغاية',
    from: 'Alger Centre',
    to: 'Réghaïa',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '20:00',
    frequency: 'Toutes les 2h',
    stops: ['Hussein Dey', 'El Harrach', 'Bab Ezzouar', 'Dar El Beïda', 'Rouiba', 'Réghaïa'],
    schedules: [
      { departure: '06:00', arrival: '06:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '07:30', arrival: '08:15', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '09:00', arrival: '09:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '11:00', arrival: '11:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '13:00', arrival: '13:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '15:00', arrival: '15:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:45', duration: '0h45', price: 150, days: 'Tous les jours' },
      { departure: '20:00', arrival: '20:45', duration: '0h45', price: 150, days: 'Tous les jours' },
    ],
  },
  {
    id: 'sntf-alger-boudouaou',
    type: 'Train',
    name: 'Alger – Boudouaou',
    nameAr: 'الجزائر – بودواو',
    from: 'Alger Centre',
    to: 'Boudouaou',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '17:30',
    stops: ['Hussein Dey', 'El Harrach', 'Réghaïa', 'Rouiba', 'Corso', 'Boudouaou'],
    schedules: [
      { departure: '06:00', arrival: '07:15', duration: '1h15', price: 200, days: 'Tous les jours' },
      { departure: '09:00', arrival: '10:15', duration: '1h15', price: 200, days: 'Tous les jours' },
      { departure: '12:00', arrival: '13:15', duration: '1h15', price: 200, days: 'Tous les jours' },
      { departure: '15:00', arrival: '16:15', duration: '1h15', price: 200, days: 'Tous les jours' },
      { departure: '17:30', arrival: '18:45', duration: '1h15', price: 200, days: 'Tous les jours' },
    ],
  },
  {
    id: 'sntf-oran-tlemcen',
    type: 'Train',
    name: 'Oran – Tlemcen',
    nameAr: 'وهران – تلمسان',
    from: 'Oran',
    to: 'Tlemcen',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '07:00',
    lastTrain: '18:00',
    stops: ['Sidi Bel Abbès', 'Mascara', 'Tlemcen'],
    schedules: [
      { departure: '07:00', arrival: '09:15', duration: '2h15', price: 350, days: 'Tous les jours' },
      { departure: '11:00', arrival: '13:15', duration: '2h15', price: 350, days: 'Tous les jours' },
      { departure: '15:00', arrival: '17:15', duration: '2h15', price: 350, days: 'Tous les jours' },
      { departure: '18:00', arrival: '20:15', duration: '2h15', price: 350, days: 'Tous les jours' },
    ],
  },
  {
    id: 'sntf-constantine-annaba',
    type: 'Train',
    name: 'Constantine – Annaba',
    nameAr: 'قسنطينة – عنابة',
    from: 'Constantine',
    to: 'Annaba',
    operator: 'SNTF',
    color: 'bg-primary-500',
    firstTrain: '06:00',
    lastTrain: '18:00',
    stops: ['Guelma', 'Souk Ahras', 'Annaba'],
    schedules: [
      { departure: '06:00', arrival: '07:30', duration: '1h30', price: 300, days: 'Tous les jours' },
      { departure: '09:00', arrival: '10:30', duration: '1h30', price: 300, days: 'Tous les jours' },
      { departure: '12:00', arrival: '13:30', duration: '1h30', price: 300, days: 'Tous les jours' },
      { departure: '15:00', arrival: '16:30', duration: '1h30', price: 300, days: 'Tous les jours' },
      { departure: '18:00', arrival: '19:30', duration: '1h30', price: 300, days: 'Tous les jours' },
    ],
  },
  // METRO
  {
    id: 'metro-l1',
    type: 'Métro',
    name: 'Métro Alger – Ligne 1',
    nameAr: 'مترو الجزائر – الخط 1',
    from: 'Tafourah',
    to: 'Aïn Naâdja',
    operator: 'ETUSA Métro',
    color: 'bg-blue-500',
    firstTrain: '05:30',
    lastTrain: '23:00',
    frequency: 'Toutes les 4 min (pointe) / 8 min (creuse)',
    stops: [
      'Tafourah', 'Khelifa Boukhalfa', 'Place des Martyrs', 'Grande Poste',
      'Aïssat Idir', 'Hamma', 'Jardin d\'Essai', 'El Harrach Centre',
      'El Harrach Gare', 'Les Fusillés', 'Bachdjarah', 'Bachdjarah Virgule',
      'Ain Naadja', 'Haï El Badr', 'Mer et Soleil',
    ],
    schedules: [
      { departure: '05:30', arrival: '06:00', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '06:00', arrival: '06:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '06:30', arrival: '07:00', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '07:00', arrival: '07:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '07:30', arrival: '08:00', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '08:00', arrival: '08:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '12:00', arrival: '12:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '18:00', arrival: '18:30', duration: '30min', price: 50, days: 'Tous les jours' },
      { departure: '22:00', arrival: '22:30', duration: '30min', price: 50, days: 'Tous les jours' },
    ],
    notes: 'Fréquence de 4 min aux heures de pointe (7h-9h et 17h-19h). Ticket valable 1h.',
  },
  // TRAMS
  {
    id: 'tram-alger-t1',
    type: 'Tram',
    name: 'Tram Alger – T1',
    nameAr: 'ترامواي الجزائر T1',
    from: 'Les Bananiers',
    to: 'Hussein Dey',
    operator: 'SETRAM Alger',
    color: 'bg-amber-500',
    firstTrain: '05:30',
    lastTrain: '22:30',
    frequency: 'Toutes les 6 min (pointe) / 12 min (creuse)',
    stops: ['Les Bananiers', 'Dergana', 'Bordj El Kiffan', 'Ain Taya', 'Hussein Dey'],
    schedules: [
      { departure: '05:30', arrival: '06:05', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '06:00', arrival: '06:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '07:00', arrival: '07:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '08:00', arrival: '08:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '12:00', arrival: '12:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '18:00', arrival: '18:35', duration: '35min', price: 50, days: 'Tous les jours' },
      { departure: '22:00', arrival: '22:35', duration: '35min', price: 50, days: 'Tous les jours' },
    ],
  },
  {
    id: 'tram-oran-t2',
    type: 'Tram',
    name: 'Tram Oran – T2',
    nameAr: 'ترامواي وهران T2',
    from: 'Oran Centre',
    to: 'Es Sénia',
    operator: 'SETRAM Oran',
    color: 'bg-amber-500',
    firstTrain: '06:00',
    lastTrain: '22:00',
    frequency: 'Toutes les 8 min',
    stops: ['Oran Centre', 'Gambetta', 'Plateau', 'Bir El Djir', 'Es Sénia'],
    schedules: [
      { departure: '06:00', arrival: '06:40', duration: '40min', price: 50, days: 'Tous les jours' },
      { departure: '07:00', arrival: '07:40', duration: '40min', price: 50, days: 'Tous les jours' },
      { departure: '08:00', arrival: '08:40', duration: '40min', price: 50, days: 'Tous les jours' },
      { departure: '12:00', arrival: '12:40', duration: '40min', price: 50, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:40', duration: '40min', price: 50, days: 'Tous les jours' },
      { departure: '21:00', arrival: '21:40', duration: '40min', price: 50, days: 'Tous les jours' },
    ],
    notes: 'Service réduit en cours entre Oran Centre et Plateau (travaux).',
  },
  {
    id: 'tram-constantine-t3',
    type: 'Tram',
    name: 'Tram Constantine – T3',
    nameAr: 'ترامواي قسنطينة T3',
    from: 'Constantine Gare',
    to: 'Zouaghi',
    operator: 'SETRAM Constantine',
    color: 'bg-amber-500',
    firstTrain: '06:00',
    lastTrain: '21:00',
    frequency: 'Toutes les 10 min',
    stops: ['Constantine Gare', 'Sidi Mabrouk', 'Zouaghi', 'Ali Mendjeli'],
    schedules: [
      { departure: '06:00', arrival: '06:28', duration: '28min', price: 50, days: 'Tous les jours' },
      { departure: '07:00', arrival: '07:28', duration: '28min', price: 50, days: 'Tous les jours' },
      { departure: '08:00', arrival: '08:28', duration: '28min', price: 50, days: 'Tous les jours' },
      { departure: '12:00', arrival: '12:28', duration: '28min', price: 50, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:28', duration: '28min', price: 50, days: 'Tous les jours' },
      { departure: '20:00', arrival: '20:28', duration: '28min', price: 50, days: 'Tous les jours' },
    ],
  },
  {
    id: 'tram-setif-t5',
    type: 'Tram',
    name: 'Tram Sétif – T5',
    nameAr: 'ترامواي سطيف T5',
    from: 'Sétif Centre',
    to: 'Aïn Arnat',
    operator: 'SETRAM Sétif',
    color: 'bg-amber-500',
    firstTrain: '06:00',
    lastTrain: '21:00',
    frequency: 'Toutes les 12 min',
    stops: ['Sétif Centre', 'Sétif Est', 'Aïn Arnat'],
    schedules: [
      { departure: '06:00', arrival: '06:22', duration: '22min', price: 50, days: 'Tous les jours' },
      { departure: '07:00', arrival: '07:22', duration: '22min', price: 50, days: 'Tous les jours' },
      { departure: '12:00', arrival: '12:22', duration: '22min', price: 50, days: 'Tous les jours' },
      { departure: '17:00', arrival: '17:22', duration: '22min', price: 50, days: 'Tous les jours' },
    ],
    notes: 'Extension de ligne en cours. Arrêts temporaires modifiés.',
  },
  {
    id: 'tram-annaba-t4',
    type: 'Tram',
    name: 'Tram Annaba – T4',
    nameAr: 'ترامواي عنابة T4',
    from: 'Annaba Gare',
    to: 'Berrahal',
    operator: 'SETRAM Annaba',
    color: 'bg-amber-500',
    firstTrain: '06:00',
    lastTrain: '20:00',
    frequency: 'Toutes les 15 min',
    stops: ['Annaba Gare', 'El Bouni', 'Berrahal'],
    schedules: [
      { departure: '06:00', arrival: '06:30', duration: '30min', price: 50, days: 'Lun-Sam' },
      { departure: '08:00', arrival: '08:30', duration: '30min', price: 50, days: 'Lun-Sam' },
      { departure: '12:00', arrival: '12:30', duration: '30min', price: 50, days: 'Lun-Sam' },
      { departure: '17:00', arrival: '17:30', duration: '30min', price: 50, days: 'Lun-Sam' },
    ],
    notes: 'Service suspendu actuellement — panne électrique. Reprise prévue prochainement.',
  },
];

// ============ SVG ICONS ============
const TrainIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect width="16" height="16" x="4" y="3" rx="2" />
    <path d="M4 11h16" />
    <path d="M12 3v8" />
    <path d="m8 19-2 3" />
    <path d="m18 22-2-3" />
    <path d="M8 15h.01" />
    <path d="M16 15h.01" />
  </svg>
);

const MetroIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const MapPinIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ClockIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const ArrowRightIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const MoonIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
  </svg>
);

const SunIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MenuIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 5h16" />
    <path d="M4 12h16" />
    <path d="M4 19h16" />
  </svg>
);

const XIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const MailIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
    <rect x="2" y="4" width="20" height="16" rx="2" />
  </svg>
);

const InstagramIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const InfoIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

// ============ NAVBAR ============
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(true);

  useEffect(() => {
    if (dark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [dark]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900 shadow-lg">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          <Link href="/home-page" className="flex items-center gap-2.5 group">
            <Image src="https://transitdz.site/_next/image?url=%2Fassets%2Fimages%2Fapp_logo.png&w=96&q=85" alt="Logo" width={36} height={36} className="flex-shrink-0" unoptimized />
            <div className="flex flex-col leading-none">
              <span className="font-bold text-lg tracking-tight text-white">Transit DZ</span>
              <span className="text-[10px] font-medium tracking-wider text-green-300">نقل الجزائر</span>
            </div>
          </Link>
          <div className="hidden lg:flex items-center gap-1">
            <Link href="/home-page" className="px-4 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-all">Accueil</Link>
            <Link href="/search-results" className="px-4 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-all">Rechercher</Link>
            <Link href="/transport-schedule" className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/20 text-white transition-all">Horaires</Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDark(d => !d)} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all">
              {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            </button>
            <button onClick={() => setMobileOpen(o => !o)} className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10">
              {mobileOpen ? <XIcon size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>
      </div>
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${mobileOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-gray-900 border-t border-gray-800 px-4 py-4 space-y-1">
          <Link href="/home-page" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:bg-gray-800">Accueil</Link>
          <Link href="/search-results" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:bg-gray-800">Rechercher</Link>
          <Link href="/transport-schedule" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-primary-600/20 text-primary-400">Horaires</Link>
        </div>
      </div>
    </nav>
  );
}

// ============ SCHEDULE CARD ============
function ScheduleCard({ line }: { line: LineSchedule }) {
  const [expanded, setExpanded] = useState(false);

  const typeColor = line.type === 'Train' ? 'text-primary-500 bg-primary-50 dark:bg-primary-900/20'
    : line.type === 'Métro'? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' :'text-amber-500 bg-amber-50 dark:bg-amber-900/20';

  const typeIcon = line.type === 'Métro' ? <MetroIcon size={16} /> : <TrainIcon size={16} />;

  const isSuspended = line.notes?.includes('suspendu') || line.notes?.includes('Suspendu');

  return (
    <div className={`card overflow-hidden hover:shadow-card-hover ${isSuspended ? 'border-red-200 dark:border-red-900/30' : ''}`}>
      {/* Header */}
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColor}`}>
              {typeIcon}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-extrabold text-gray-900 dark:text-white text-base">{line.name}</h3>
                {isSuspended && (
                  <span className="text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-md">Suspendu</span>
                )}
              </div>
              <p className="text-xs text-gray-500" dir="rtl" lang="ar">{line.nameAr}</p>
            </div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg ${typeColor}`}>
            {line.operator}
          </span>
        </div>

        {/* Route */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary-500" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{line.from}</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <ArrowRightIcon size={14} className="text-gray-400" />
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary-500" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{line.to}</span>
          </div>
        </div>

        {/* Info Row */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          {line.firstTrain && (
            <span className="flex items-center gap-1">
              <ClockIcon size={11} className="text-primary-400" />
              Premier: {line.firstTrain}
            </span>
          )}
          {line.lastTrain && (
            <span className="flex items-center gap-1">
              <ClockIcon size={11} className="text-gray-400" />
              Dernier: {line.lastTrain}
            </span>
          )}
          {line.frequency && (
            <span className="flex items-center gap-1">
              <TrainIcon size={11} className="text-primary-400" />
              {line.frequency}
            </span>
          )}
        </div>

        {/* Notes */}
        {line.notes && (
          <div className={`mt-3 flex items-start gap-2 p-3 rounded-xl text-xs ${
            isSuspended ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
          }`}>
            <InfoIcon size={12} className="flex-shrink-0 mt-0.5" />
            {line.notes}
          </div>
        )}
      </div>

      {/* Stops */}
      <div className="px-5 pb-4">
        <div className="flex flex-wrap gap-1.5">
          {line.stops.map((s, i) => (
            <span key={s} className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
              {i > 0 && <span className="text-gray-300 dark:text-gray-600">→</span>}
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Schedule Table */}
      <div className="border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <span>Voir les horaires ({line.schedules.length} départs)</span>
          <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {expanded && (
          <div className="px-5 pb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Départ</th>
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Arrivée</th>
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Durée</th>
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Tarif</th>
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Jours</th>
                  </tr>
                </thead>
                <tbody>
                  {line.schedules.map((s, i) => (
                    <tr key={i} className="border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="py-2.5 font-bold text-gray-900 dark:text-white">{s.departure}</td>
                      <td className="py-2.5 text-gray-600 dark:text-gray-400">{s.arrival}</td>
                      <td className="py-2.5">
                        <span className="flex items-center gap-1 text-gray-500">
                          <ClockIcon size={11} />{s.duration}
                        </span>
                      </td>
                      <td className="py-2.5">
                        <span className="font-bold text-primary-600 dark:text-primary-400">{s.price} DZD</span>
                      </td>
                      <td className="py-2.5 text-xs text-gray-400">{s.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ TRANSPORT SCHEDULE INNER ============
function TransportScheduleInner() {
  const searchParams = useSearchParams();
  const [activeType, setActiveType] = useState<'all' | 'Train' | 'Métro' | 'Tram'>(
    (searchParams.get('type') as 'Train' | 'Métro' | 'Tram') || 'all'
  );
  const [search, setSearch] = useState('');

  const filtered = SCHEDULES.filter(s => {
    const matchType = activeType === 'all' || s.type === activeType;
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.from.toLowerCase().includes(search.toLowerCase()) ||
      s.to.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const counts = {
    all: SCHEDULES.length,
    Train: SCHEDULES.filter(s => s.type === 'Train').length,
    Métro: SCHEDULES.filter(s => s.type === 'Métro').length,
    Tram: SCHEDULES.filter(s => s.type === 'Tram').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navbar />

      {/* Header */}
      <div className="bg-gray-900 pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-primary-400 text-xs font-semibold uppercase tracking-wider mb-2">Horaires · جداول المواصلات</p>
              <h1 className="text-3xl font-extrabold text-white">Tous les horaires</h1>
              <p className="text-gray-400 text-sm mt-1">Trains SNTF, Métro Alger et Tramways</p>
            </div>
            <Link href="/search-results" className="btn-primary text-sm px-5 py-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m21 21-4.34-4.34" />
                <circle cx="11" cy="11" r="8" />
              </svg>
              Rechercher un trajet
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Type Filter */}
            <div className="flex flex-wrap gap-2">
              {(['all', 'Train', 'Métro', 'Tram'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setActiveType(t)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                    activeType === t
                      ? 'bg-primary-500 text-white shadow-green-glow'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t === 'all' ? 'Tous' : t}
                  <span className="ml-1.5 text-[10px] opacity-70">({counts[t === 'all' ? 'all' : t]})</span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
                  <path d="m21 21-4.34-4.34" />
                  <circle cx="11" cy="11" r="8" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher une ligne..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
        {filtered.length === 0 ? (
          <div className="card p-12 text-center">
            <TrainIcon size={48} className="text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Aucune ligne trouvée</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Essayez un autre terme de recherche.</p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              <span className="font-bold text-gray-900 dark:text-white">{filtered.length}</span> ligne{filtered.length > 1 ? 's' : ''} trouvée{filtered.length > 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map(line => (
                <ScheduleCard key={line.id} line={line} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Image src="https://transitdz.site/_next/image?url=%2Fassets%2Fimages%2Fapp_logo.png&w=96&q=85" alt="Logo" width={32} height={32} unoptimized />
              <span className="font-bold text-white">Transit DZ</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="mailto:transitdz0@gmail.com" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                <MailIcon size={12} />transitdz0@gmail.com
              </a>
              <a href="https://www.instagram.com/transit.dz" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                <InstagramIcon size={12} />@transit.dz
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2026 Transit DZ 🇩🇿</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function TransportSchedulePage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary-500/30 border-t-primary-500 animate-spin" />
      </div>
    }>
      <TransportScheduleInner />
    </Suspense>
  );
}
